export const termsOfServiceContent = {
  'en-US': {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: May 21, 2024',
    description:
      'Please read the following terms carefully. By accessing or using the Platform you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.',
    sections: [
      {
        id: 'introduction',
        title: '1. Introduction',
        paragraphs: [
          'Welcome to the Trading Backtest Platform ("the Platform"). These Terms of Service describe the rights and obligations between you ("the user") and the Platform when you use our services. Our goal is to provide an efficient and convenient tool for learning, testing, and evaluating securities trading strategies.'
        ]
      },
      {
        id: 'user-obligations',
        title: '2. User Obligations',
        paragraphs: [
          'You agree to use all features and services provided by the Platform in a lawful and compliant manner. You must not use the Platform for any illegal activity, including but not limited to market manipulation, insider trading, or distributing false information.',
          'You are fully responsible for all activity that occurs under your account and must safeguard your credentials (including session information issued through Casdoor or other third-party authentication services) to prevent unauthorised access or use.',
          'You must not attempt to reverse engineer, decompile, or otherwise obtain the Platform’s source code or core algorithms, nor may you interfere with or attempt to disrupt the normal operation of the Platform.'
        ]
      },
      {
        id: 'risk-disclaimer',
        title: '3. Risk Disclaimer',
        paragraphs: [
          'All simulated trading results, historical data, chart analytics, and related information provided by the Platform are for educational, learning, and research purposes only. They do not constitute investment advice, recommendations, or guidance. Financial markets are inherently uncertain, and past performance does not guarantee future outcomes.',
          'You acknowledge the substantial differences between simulated and real trading, including execution speed, slippage, liquidity, market impact, and psychological factors. Any real investment decision you make based on information from the Platform is undertaken at your own risk. The Platform accepts no liability.',
          'Data sources used by the Platform may be delayed, inaccurate, or incomplete. The Platform makes no warranties in this regard and bears no responsibility for any losses resulting from data issues.'
        ]
      },
      {
        id: 'liability-limitation',
        title: '4. Limitation of Liability',
        paragraphs: [
          'To the maximum extent permitted by applicable law, the Platform and its affiliates or developers shall not be liable for any direct, indirect, incidental, special, punitive, or consequential damages (including but not limited to loss of profit, data, or business interruption) arising from your use of, or inability to use, the services, even if advised of the possibility of such damages.',
          'While the Platform endeavours to maintain service stability and data accuracy, it provides no express or implied warranties, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.',
          'If the Platform is required to bear liability for any reason, the total aggregate compensation will not exceed the lesser of the fees (if any) you paid for the services during the preceding three months or HKD 100.'
        ]
      },
      {
        id: 'intellectual-property',
        title: '5. Intellectual Property',
        paragraphs: [
          'All content on the Platform—including software, text, graphics, logos, icons, images, audio and video clips, data compilations, and underlying algorithms—is the property of the Platform or its content providers and is protected by international copyright and other intellectual property laws.',
          'You may not copy, distribute, modify, create derivative works from, publicly display or perform, republish, download, store, or transmit any material from the Platform unless you have explicit written permission from the Platform.'
        ]
      },
      {
        id: 'privacy-policy-reference',
        title: '6. Privacy Policy',
        paragraphs: [
          {
            type: 'privacy',
            prefix:
              'We take user privacy seriously. For details on how we collect, use, and protect your personal information, please refer to our ',
            linkLabel: 'Privacy Policy',
            linkTo: '/privacy-policy',
            suffix: '. The Privacy Policy is an integral part of these Terms of Service.'
          }
        ]
      },
      {
        id: 'service-modification-termination',
        title: '7. Service Modification, Suspension, or Termination',
        paragraphs: [
          'The Platform reserves the right to modify, suspend, or permanently discontinue all or part of the services at any time, with or without notice. You agree that the Platform is not liable to you or any third party for any such changes.',
          'If you violate any provision of these Terms, the Platform may immediately suspend or terminate your account and access to the services without prior notice.'
        ]
      },
      {
        id: 'applicable-law',
        title: '8. Governing Law and Jurisdiction',
        paragraphs: [
          'These Terms, including their formation, effectiveness, performance, interpretation, and dispute resolution, are governed by the laws of the Hong Kong Special Administrative Region of the People’s Republic of China ("Hong Kong"). Where no relevant law applies, international commercial practices and/or industry practices shall be referenced.',
          'Any dispute arising from or relating to these Terms shall first be resolved through friendly negotiation. If negotiations fail, either party may submit the dispute to the competent courts of Hong Kong.'
        ]
      },
      {
        id: 'miscellaneous',
        title: '9. Miscellaneous',
        paragraphs: [
          'The Platform may amend these Terms at any time. Updated terms take effect immediately once published on the Platform. Continuing to use the services constitutes acceptance of the revised Terms. You are encouraged to review the Terms regularly.',
          'Section headings are provided for convenience only and do not affect the interpretation of the provisions. If any provision is deemed invalid or unenforceable by a court of competent jurisdiction, the remaining provisions remain in full force and effect.',
          'For matters not covered by these Terms, relevant national laws, regulations, and policies shall apply.'
        ]
      }
    ]
  },
  'zh-CN': {
    title: '服务条款',
    lastUpdated: '最后更新日期：2024年5月21日',
    description:
      '请仔细阅读以下服务条款。当您访问或使用本平台，即表示您已阅读、理解并同意受本服务条款的约束。',
    sections: [
      {
        id: 'introduction',
        title: '1. 简介',
        paragraphs: [
          '欢迎使用证券交易回测平台（以下简称“本平台”）。本服务条款旨在明确您（以下简称“用户”）在使用本平台提供的服务时的权利与义务。本平台致力于为用户提供一个高效、便捷的工具，用于学习、测试和评估证券交易策略。'
        ]
      },
      {
        id: 'user-obligations',
        title: '2. 用户义务',
        paragraphs: [
          '用户承诺将以合法、合规的方式使用本平台提供的所有功能与服务。用户不得利用本平台进行任何非法活动，包括但不限于市场操纵、内幕交易、传播虚假信息等。',
          '用户应对其账户下的所有活动负全部责任，并妥善保管账户信息（包括通过 Casdoor 等第三方认证服务生成的会话信息），防止未经授权的访问或使用。',
          '用户不得尝试反向工程、破解或以其他方式获取本平台源代码或核心算法。用户不得干扰或试图干扰本平台的正常运行。'
        ]
      },
      {
        id: 'risk-disclaimer',
        title: '3. 风险声明',
        paragraphs: [
          '本平台提供的所有模拟交易结果、历史数据、图表分析及其他信息仅为教育、学习和研究目的，不构成任何真实的投资建议、推荐或指导。金融市场充满不确定性，历史表现不代表未来结果。',
          '用户应充分认识到模拟交易与真实交易之间存在显著差异，包括但不限于执行速度、滑点、流动性、市场冲击、以及心理因素等。任何基于本平台信息进行的实际投资决策，其风险由用户自行承担，本平台不承担任何责任。',
          '本平台使用的数据源可能存在延迟、不准确或不完整的情况，本平台对此不作任何保证，并对因数据问题导致的任何损失不承担责任。'
        ]
      },
      {
        id: 'liability-limitation',
        title: '4. 责任限制',
        paragraphs: [
          '在适用法律允许的最大范围内，本平台及其关联方、开发者不对因用户使用或无法使用本平台服务而导致的任何直接、间接、偶然、特殊、惩戒性或后果性损害（包括但不限于利润损失、数据丢失或业务中断）承担责任，即使已被告知此类损害的可能性。',
          '本平台将尽力确保服务的稳定性和数据的准确性，但不作任何明示或暗示的保证，包括但不限于对服务的适销性、特定用途适用性及不侵权作出任何保证。',
          '如果本平台因任何原因需要承担责任，则总赔偿金额不超过用户在过去三个月内为使用本服务所支付的费用（如有）或100港元中的较低者。'
        ]
      },
      {
        id: 'intellectual-property',
        title: '5. 知识产权',
        paragraphs: [
          '本平台及其所有内容，包括但不限于软件、文本、图形、徽标、图标、图像、音频剪辑、视频剪辑、数据汇编和基础算法，均为本平台或其内容供应商的财产，并受国际版权法和其他知识产权法律的保护。',
          '用户不得复制、分发、修改、创作衍生作品、公开展示、公开表演、重新发布、下载、存储或传输本平台上的任何材料，除非得到本平台的明确书面许可。'
        ]
      },
      {
        id: 'privacy-policy-reference',
        title: '6. 隐私政策',
        paragraphs: [
          {
            type: 'privacy',
            prefix: '本平台重视用户隐私。关于我们如何收集、使用和保护您的个人信息，请参阅我们的',
            linkLabel: '隐私政策',
            linkTo: '/privacy-policy',
            suffix: '。隐私政策构成本服务条款不可分割的一部分。'
          }
        ]
      },
      {
        id: 'service-modification-termination',
        title: '7. 服务变更、中断或终止',
        paragraphs: [
          '本平台保留随时修改、暂停或永久终止提供部分或全部服务的权利，无论是否通知用户。用户同意本平台无需因服务的修改、暂停或终止对用户或任何第三方承担责任。',
          '若用户违反本服务条款的任何规定，本平台有权立即暂停或终止该用户的账户及服务访问权限，恕不另行通知。'
        ]
      },
      {
        id: 'applicable-law',
        title: '8. 法律适用与管辖',
        paragraphs: [
          '本服务条款的订立、生效、履行、解释及争议解决，均应适用中华人民共和国香港特别行政区（以下简称“香港”）法律。若无相关法律规定，则应参照国际商业惯例和/或行业惯例。',
          '任何因本服务条款引起的或与本服务条款有关的争议，双方应首先友好协商解决；协商不成的，任何一方均有权将争议提交至香港有管辖权的法院通过诉讼解决。'
        ]
      },
      {
        id: 'miscellaneous',
        title: '9. 其他',
        paragraphs: [
          '本平台保留随时修改本服务条款的权利。修改后的条款将在平台公布后立即生效。用户继续使用本平台服务即视为接受修改后的条款。建议用户定期查阅本条款以了解最新内容。',
          '本条款中的标题仅为阅读方便而设，不影响本条款任何规定的解释。若本条款中的任何规定被有管辖权的法院认定为无效或不可执行，该等规定的无效或不可执行不影响本条款其余规定的效力，其余规定应继续维持完整的效力和作用。',
          '本条款未尽事宜，应参照国家相关法律法规及政策执行。'
        ]
      }
    ]
  }
};
